/**
 * Pure helpers for Image Editor rulers / grid / snap overlay.
 * Overlay chrome only — never baked into the working PNG buffer.
 */

/** Pixel distance (canvas space) within which snap engages. */
export const SNAP_THRESHOLD_PX = 6;

export const GRID_SIZE_OPTIONS = [8, 16, 32];

/** Viewport zoom bounds (CSS only — never changes buffer resolution). */
export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 4;
export const ZOOM_STEP = 0.25;

/**
 * Zoom that fits the full canvas inside the available viewport (CSS zoom only).
 * Floors to ZOOM_STEP so rounding never clips the canvas.
 *
 * @param {number} canvasWidth
 * @param {number} canvasHeight
 * @param {number} viewportWidth available content width (padding already subtracted)
 * @param {number} viewportHeight available content height (padding already subtracted)
 * @returns {number}
 */
export function computeFitZoom(
  canvasWidth,
  canvasHeight,
  viewportWidth,
  viewportHeight,
) {
  const cw = Math.max(1, Number(canvasWidth) || 1);
  const ch = Math.max(1, Number(canvasHeight) || 1);
  const vw = Math.max(1, Number(viewportWidth) || 1);
  const vh = Math.max(1, Number(viewportHeight) || 1);
  const scale = Math.min(vw / cw, vh / ch);
  const stepped = Math.floor(scale / ZOOM_STEP + 1e-9) * ZOOM_STEP;
  if (!Number.isFinite(stepped) || stepped < ZOOM_MIN) return ZOOM_MIN;
  return clampZoom(stepped);
}

/**
 * @param {number} value
 * @param {number} max inclusive upper bound (typically width-1 or height-1)
 * @returns {number}
 */
export function clampCanvasAxis(value, max) {
  const m = Math.max(0, Math.floor(Number(max) || 0));
  const v = Math.round(Number(value) || 0);
  return Math.max(0, Math.min(m, v));
}

/**
 * @param {number} zoom
 * @returns {number}
 */
export function clampZoom(zoom) {
  const z = Number(zoom);
  if (!Number.isFinite(z)) return 1;
  return Math.min(
    ZOOM_MAX,
    Math.max(ZOOM_MIN, Math.round(z / ZOOM_STEP) * ZOOM_STEP),
  );
}

/**
 * Sanitize a user-entered filename stem (whole name — no forced prefix).
 * Strips path separators, trailing extension, and unsafe characters.
 * @param {unknown} raw
 * @param {string} [fallback]
 * @returns {string}
 */
export function sanitizeImageEditorFileStem(raw, fallback = 'untitled') {
  let s = String(raw || '').trim();
  if (!s) return fallback;
  // Reject path traversal / separators.
  if (/[/\\]/.test(s)) {
    s = s.replace(/[/\\]+/g, '');
  }
  s = s
    .replace(/\.[^.\\/]+$/, '') // drop one trailing extension if typed
    .replace(/[<>:"|?*\u0000-\u001f]+/g, '_')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
  return s || fallback;
}

/**
 * Sanitize a user-entered save suffix (legacy helper; prefer free filename).
 * @param {unknown} raw
 * @returns {string}
 */
export function sanitizeAssetSuffix(raw) {
  return String(raw || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
}

/**
 * Live preview for a free-text filename + format extension.
 * @param {unknown} rawFilename whole name the user typed (extension optional)
 * @param {unknown} [format] png | webp | jpeg
 * @returns {{ fileStem: string, fileName: string, extension: string, valid: boolean }}
 */
export function buildSaveFilePreview(rawFilename, format = 'png') {
  const fileStem = sanitizeImageEditorFileStem(rawFilename, '');
  const normalized = String(format || 'png')
    .trim()
    .toLowerCase();
  const extension =
    normalized === 'webp'
      ? 'webp'
      : normalized === 'jpeg' || normalized === 'jpg'
        ? 'jpg'
        : 'png';
  return {
    fileStem,
    extension,
    fileName: fileStem ? `${fileStem}.${extension}` : '',
    valid: Boolean(fileStem),
  };
}

/**
 * Snap a single axis coordinate to the nearest grid line and/or guide.
 * When snap is off, returns the clamped rounded value unchanged (aside from clamp).
 *
 * @param {number} value canvas-space coordinate
 * @param {{
 *   snapEnabled?: boolean,
 *   showGrid?: boolean,
 *   gridSize?: number,
 *   guides?: number[],
 *   max: number,
 *   threshold?: number,
 * }} opts
 * @returns {number}
 */
export function snapAxisValue(value, opts) {
  const max = opts && typeof opts.max === 'number' ? opts.max : 0;
  const raw = clampCanvasAxis(value, max);
  if (!opts || !opts.snapEnabled) return raw;

  const threshold =
    typeof opts.threshold === 'number' && opts.threshold >= 0
      ? opts.threshold
      : SNAP_THRESHOLD_PX;

  /** @type {number[]} */
  const candidates = [];
  const gridSize = Number(opts.gridSize) || 0;
  if (opts.showGrid && gridSize > 0) {
    const stepped = Math.round(raw / gridSize) * gridSize;
    candidates.push(clampCanvasAxis(stepped, max));
    candidates.push(clampCanvasAxis(stepped - gridSize, max));
    candidates.push(clampCanvasAxis(stepped + gridSize, max));
  }

  const guides = Array.isArray(opts.guides) ? opts.guides : [];
  guides.forEach((g) => {
    if (typeof g === 'number' && Number.isFinite(g)) {
      candidates.push(clampCanvasAxis(g, max));
    }
  });

  if (!candidates.length) return raw;

  let best = raw;
  let bestDist = threshold;
  candidates.forEach((c) => {
    const d = Math.abs(c - raw);
    if (d <= bestDist) {
      bestDist = d;
      best = c;
    }
  });
  return best;
}

/**
 * Snap a canvas point for paint / future marquee+float tools.
 *
 * @param {{x:number,y:number}} pt
 * @param {{
 *   snapEnabled?: boolean,
 *   showGrid?: boolean,
 *   gridSize?: number,
 *   vGuides?: number[],
 *   hGuides?: number[],
 *   width: number,
 *   height: number,
 *   threshold?: number,
 * }} opts
 * @returns {{x:number,y:number}}
 */
export function snapCanvasPoint(pt, opts) {
  if (!pt || typeof pt !== 'object') return { x: 0, y: 0 };
  const width = opts && typeof opts.width === 'number' ? opts.width : 0;
  const height = opts && typeof opts.height === 'number' ? opts.height : 0;
  const xMax = Math.max(0, width - 1);
  const yMax = Math.max(0, height - 1);
  return {
    x: snapAxisValue(pt.x, {
      snapEnabled: opts?.snapEnabled,
      showGrid: opts?.showGrid,
      gridSize: opts?.gridSize,
      guides: opts?.vGuides,
      max: xMax,
      threshold: opts?.threshold,
    }),
    y: snapAxisValue(pt.y, {
      snapEnabled: opts?.snapEnabled,
      showGrid: opts?.showGrid,
      gridSize: opts?.gridSize,
      guides: opts?.hGuides,
      max: yMax,
      threshold: opts?.threshold,
    }),
  };
}
